import React, { useCallback, useEffect, useState } from "react";
import { ConfigProvider, Drawer } from "antd";
import { useLocation } from "react-router-dom";

import LogoWhite from "../../assets/image/logo-white.svg";
import ErrorBoundary from "../component/ErrorBoundary";
import { antdLocaleFor } from "../theme/antd";
import AppBar from "./AppBar";
import AppNav from "./AppNav";

import "./shell.scss";

/** Matches the `max-width: 900px` breakpoint in shell.scss, where the sidebar becomes a drawer. */
const MOBILE_QUERY = "(max-width: 900px)";

function Brand() {
  return (
    <div className="shell-brand">
      <img src={LogoWhite} alt="Picsell" />
    </div>
  );
}

/**
 * The application frame every signed-in page renders inside: sidebar + app bar + content.
 * Pages supply only their own content — they must not render their own header or nav.
 *
 * Below 900px the sidebar becomes an overlay drawer opened from the app bar's burger.
 */
export default function AppShell({ lang, children }) {
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const sync = e => {
      setIsMobile(e.matches);
      /* Widening past the breakpoint leaves the drawer mounted over a sidebar that is now
         visible anyway — close it so there is exactly one nav on screen. */
      if (!e.matches) setNavOpen(false);
    };
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  /* index.html hardcodes lang="en", so without this the document keeps claiming English no
     matter which language the user picked. Screen-reader pronunciation and the Armenian /
     Georgian font fallbacks both key off it. */
  useEffect(() => {
    if (lang) document.documentElement.lang = lang;
  }, [lang]);

  /* Navigating from inside the drawer should dismiss it. */
  useEffect(() => setNavOpen(false), [location.pathname]);

  const closeNav = useCallback(() => setNavOpen(false), []);

  return (
    <ConfigProvider locale={antdLocaleFor(lang)}>
      {/* The `theme-1-light` / `app-body` compatibility shims that used to sit here are gone with
          the legacy stylesheets that hung rules off them as ancestors. */}
      <div className="shell">
        {isMobile ? (
          <Drawer
            open={navOpen}
            onClose={closeNav}
            placement="left"
            width={260}
            closable={false}
            classNames={{ body: "shell-drawer__body" }}
            styles={{ body: { padding: 0 } }}>
            <Brand />
            <AppNav onNavigate={closeNav} />
          </Drawer>
        ) : (
          <aside className="shell-side">
            <Brand />
            <AppNav />
          </aside>
        )}

        <div className="shell-main">
          <AppBar onOpenNav={() => setNavOpen(true)} />
          {/* Keyed by pathname: a page crash stays contained (the nav keeps working) and the
              boundary resets when the route changes. */}
          <div>
            <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}
